import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';

/**
 * /c/:slug — resolves a campaign short link.
 *
 * Calls /public/click/:slug to (a) record the click and (b) get the
 * original URL, then immediately replaces window.location. We deliberately
 * render almost nothing — anyone landing here is on their way somewhere
 * else, so visual flicker is the goal to minimise.
 *
 * If the lookup fails (404, network error), we send the user to the
 * amutech.co.il home page rather than showing a dead-end "not found" —
 * the SMS sender's contact got the URL from a legitimate campaign and
 * deserves a graceful exit.
 */
const ShortLinkRedirectPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) {
      window.location.replace('/');
      return;
    }
    // Pass the true source (document.referrer) — the XHR's own Referer header
    // would just be this /c/ page. Backend records it as the click's source.
    const ref = document.referrer ? `?ref=${encodeURIComponent(document.referrer)}` : '';
    api
      .get(`/public/click/${encodeURIComponent(slug)}${ref}`, {
        headers: { Accept: 'application/json' },
      })
      .then((r) => {
        const url = r?.data?.url;
        if (typeof url === 'string' && /^https?:\/\//i.test(url)) {
          window.location.replace(url);
        } else {
          window.location.replace('/');
        }
      })
      .catch(() => {
        // Don't loop forever — show a short error state, then home.
        setError(true);
        setTimeout(() => window.location.replace('/'), 1500);
      });
  }, [slug]);

  return (
    <div dir="rtl" style={{ fontFamily: 'system-ui, sans-serif', padding: 40, color: '#374151' }}>
      {error ? 'הקישור פג תוקף — מעביר אותך לעמוד הראשי…' : 'טוען…'}
    </div>
  );
};

export default ShortLinkRedirectPage;
